/**
 * Abrechnungs-Sektion eines Kunden (CustomerDetailPage): Vorschau der
 * gewählten Periode (Summen je Tenant) und Liste der bisherigen Abrechnungen.
 * Backend: GET /api/billing/customers/:id/preview, GET /api/billing/statements?customerId=
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatementDetail, StatementSummary, extractApiError, listStatements, previewCustomerStatement } from '../services/api';
import { currentPeriodKey, formatCents, periodLabel } from '../services/format';
import { PeriodPicker } from './billing/PeriodPicker';

export function CustomerBillingSection({ customerId }: { customerId: string }) {
  const [period, setPeriod] = useState(currentPeriodKey());
  const [preview, setPreview] = useState<StatementDetail | null>(null);
  const [statements, setStatements] = useState<StatementSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    previewCustomerStatement(customerId, period).then(setPreview).catch((e) => setError(extractApiError(e)));
  }, [customerId, period]);
  useEffect(() => {
    listStatements({ customerId }).then(setStatements).catch(() => setStatements([]));
  }, [customerId]);

  const perTenant = preview ? Array.from(preview.lines.reduce((m, l) => {
    const e = m.get(l.tenantSlug) ?? { revenue: 0, cost: 0 };
    if (l.side === 'revenue') e.revenue += l.amountCents; else e.cost += l.amountCents;
    m.set(l.tenantSlug, e);
    return m;
  }, new Map<string, { revenue: number; cost: number }>()).entries()) : [];

  const existing = statements.find((s) => s.periodKey === period);

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-semibold">Abrechnung</h3>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>
      {error && <div className="bg-red-100 text-red-700 p-2 rounded mb-2 text-sm">{error}</div>}
      {preview && (
        <div className="bg-white rounded shadow p-4 mb-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
            <p className="text-sm text-slate-600">
              Vorschau {periodLabel(period)}: <strong className="font-mono">{formatCents(preview.netCents)}</strong> netto ·{' '}
              {formatCents(preview.grossCents)} brutto{preview.reverseCharge ? ' (Reverse Charge)' : ''} · Kosten {formatCents(preview.costCents)} · DB {formatCents(preview.marginCents)}
            </p>
            {existing
              ? <Link to={`/billing/statements/${existing.id}`} className="text-sm text-primary-600 hover:underline">Abrechnung ({existing.status}) öffnen →</Link>
              : <Link to="/billing" className="text-sm text-primary-600 hover:underline">Abrechnungslauf in der Übersicht starten →</Link>}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left"><tr><th className="p-2">Deployment</th><th className="p-2 text-right">Erlös</th><th className="p-2 text-right">Kosten</th><th className="p-2 text-right">Deckungsbeitrag</th></tr></thead>
            <tbody>
              {perTenant.map(([slug, v]) => (
                <tr key={slug} className="border-t">
                  <td className="p-2 font-mono"><Link to={`/tenants/${slug}?period=${period}#billing`} className="text-primary-600 hover:underline">{slug}</Link></td>
                  <td className="p-2 text-right font-mono">{formatCents(v.revenue)}</td>
                  <td className="p-2 text-right font-mono text-slate-500">{formatCents(v.cost)}</td>
                  <td className={`p-2 text-right font-mono ${v.revenue - v.cost < 0 ? 'text-red-700' : ''}`}>{formatCents(v.revenue - v.cost)}</td>
                </tr>
              ))}
              {perTenant.length === 0 && <tr><td colSpan={4} className="p-3 text-center text-slate-400">Keine abrechenbaren Deployments in dieser Periode.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {statements.length > 0 && (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left"><tr><th className="p-2">Periode</th><th className="p-2">Status</th><th className="p-2 text-right">Netto</th><th className="p-2 text-right">Brutto</th><th className="p-2">Rechnungs-Nr.</th></tr></thead>
          <tbody>
            {statements.map((s) => (
              <tr key={s.id} className="border-t">
                <td className="p-2"><Link to={`/billing/statements/${s.id}`} className="text-primary-600 hover:underline">{periodLabel(s.periodKey)}</Link></td>
                <td className="p-2"><span className="badge bg-slate-200">{s.status}</span></td>
                <td className="p-2 text-right font-mono">{formatCents(s.netCents)}</td>
                <td className="p-2 text-right font-mono">{formatCents(s.grossCents)}</td>
                <td className="p-2 font-mono text-xs">{s.externalRef ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
